import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { addHours } from 'date-fns';
import { getDuration, parseSqlDate } from '../dateTime';
/**
 * Custom hook to countdown to the target date
 * @param {string} targetDateString - the target date to countdown to, in string
 * @param {number} hoursToAdd
 * @param {string} location - Store location, example: AU, NZ, US
 * @returns {object} - the duration object with days, hours, minutes and seconds. returns null if the date is invalid or has passed
 */
export const useCountdown = ({ targetDateString, hoursToAdd = 0, location }) => {
  const targetDate = useMemo(() => addHours(parseSqlDate(targetDateString, location), hoursToAdd), [targetDateString, location, hoursToAdd]);
  const [date, setDate] = useState(new Date());
  const animationRef = useRef(null);
  const timeRef = useRef(null);

  // tick every second
  const tick = useCallback((t) => {
    if (t - timeRef.current >= 1000) {
      setDate(new Date());
      timeRef.current = t;
    }
    animationRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    animationRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [tick]);

  const duration = useMemo(() => getDuration(targetDate, date), [targetDate, date]);

  if (!targetDate || date >= targetDate) return null;

  return duration;
};
